'use client'
import Link from 'next/link'
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Trophy, ChevronRight, Crown } from 'lucide-react'
import { AchievementBadge } from './medallions/achievement-badge'
import { StarIcon } from './medallions/star-icon'

export interface LeaderboardRow {
  rank: number | null
  userId?: string
  name: string
  avatar: string
  currentStars: number
  isMe?: boolean
  delta?: string
}

export interface LeaderboardCardProps {
  /** Current user's first name, used in the All Star banner. */
  firstName: string
  /** Map of rows per scope. If a scope is omitted, that tab shows "No data yet". */
  scopes: { team?: LeaderboardRow[]; dept?: LeaderboardRow[]; company?: LeaderboardRow[] }
  seeAllHref?: string
}

export function LeaderboardCard({ firstName, scopes, seeAllHref = '/phishtrainer/leaderboard' }: LeaderboardCardProps) {
  const [scope, setScope] = useState<'team' | 'dept' | 'company'>('team')
  const rows = scopes[scope] ?? []
  const me = rows.find((r) => r.isMe)
  const meInTop = !!me && rows.slice(0, 5).some((r) => r.isMe)

  return (
    <Card className="relative overflow-hidden h-full">
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-15 blur-3xl bg-primary" />
      <CardHeader className="relative flex flex-row items-center justify-between space-y-0 gap-2 pb-3">
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-amber-500" /> Leaderboard
        </CardTitle>
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
          <Link href={seeAllHref}>See all <ChevronRight className="h-4 w-4" /></Link>
        </Button>
      </CardHeader>
      <CardContent className="relative">
        <Tabs value={scope} onValueChange={(v) => setScope(v as typeof scope)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="team">Team</TabsTrigger>
            <TabsTrigger value="dept">Department</TabsTrigger>
            <TabsTrigger value="company">Company</TabsTrigger>
          </TabsList>

          <TabsContent value={scope} className="mt-4 space-y-3">
            {me && me.rank === 1 && (
              <div className="relative overflow-hidden rounded-xl border border-amber-400/40 bg-gradient-to-br from-amber-500/20 via-yellow-500/10 to-orange-500/15 p-3">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <AchievementBadge color="#f59e0b" icon={<Crown className="h-6 w-6" />} size={56} />
                    <div className="absolute -inset-3 -z-10 rounded-full blur-2xl bg-amber-400/40" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">All Star</div>
                    <div className="text-sm font-semibold text-foreground">
                      Good job {firstName}, you&apos;re #1 this quarter!
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              {rows.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-4">No ranked players in this scope yet.</div>
              )}
              {rows.slice(0, 5).map((row, i) => <Row key={row.userId ?? row.rank ?? i} row={row} />)}
            </div>

            {me && !meInTop && (
              <div className="pt-2 border-t border-dashed border-border mt-2">
                <Row row={me} />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}

function Row({ row }: { row: LeaderboardRow }) {
  const rankColor =
    row.rank === 1 ? 'bg-gradient-to-br from-amber-400 to-yellow-600 text-white' :
    row.rank === 2 ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-white' :
    row.rank === 3 ? 'bg-gradient-to-br from-amber-700 to-orange-800 text-white' :
    'bg-muted text-muted-foreground'
  const delta = row.delta ?? '0'
  const deltaColor = delta.startsWith('+') ? 'text-emerald-500 dark:text-emerald-400'
    : delta.startsWith('-') ? 'text-red-500 dark:text-red-400'
    : 'text-muted-foreground'

  return (
    <div className={
      'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ' +
      (row.isMe ? 'bg-primary/10 ring-1 ring-primary/30' : 'hover:bg-muted/60')
    }>
      <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${rankColor}`}>{row.rank ?? '—'}</div>
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">{row.avatar}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate text-foreground">
          {row.name} {row.isMe && <span className="text-xs text-muted-foreground">(you)</span>}
        </div>
      </div>
      <div className={`text-[10px] font-semibold tabular-nums ${deltaColor}`}>{delta !== '0' ? delta : '—'}</div>
      <div className="flex items-center gap-1 font-semibold text-foreground min-w-[60px] justify-end">
        <StarIcon size={13} />
        <span className="tabular-nums">{row.currentStars}</span>
      </div>
    </div>
  )
}
